import Image from 'next/image'
import Link from 'next/link'
import { LuArrowUpLeft, LuArrowUpRight } from 'react-icons/lu'

import { DiamondIcon } from '@/components/frontend/DiamondIcon'
import { SectionPreHeader } from '@/components/frontend/SectionPreHeader'

const contentPadding = 'px-6 md:px-12'

export interface AboutHeroSectionProps {
  sectionLabel?: string | null
  moto?: string | null
  message?: string | null
  buttonLabel?: string | null
  buttonLink?: string | null
  locale: AppLocale
}

export function AboutHeroSection({
  sectionLabel,
  moto,
  message,
  buttonLabel = 'ABOUT US',
  buttonLink = '/about',
  locale,
}: AboutHeroSectionProps) {
  return (
    <>
      <section className={`flex justify-center bg-white pt-2 pb-4 md:py-8 ${contentPadding}`}>
        <Image
          src="/images/since2012.svg"
          alt="Since 2012"
          width={889}
          height={203}
          className="h-auto w-full max-w-[min(100%,28rem)] object-contain"
          priority
        />
      </section>

      <section
        className={`mb-16 sm:mb-16 bg-white pt-10 pb-16 sm:pt-24 sm:pb-24 lg:py-32 md:mb-24 ${contentPadding}`}
      >
        <div className="mx-auto grid max-w-6xl grid-cols-1 items-stretch gap-6 sm:gap-8 lg:grid-cols-[auto,1.1fr]">
          <div className="relative hidden min-h-[120px] flex-col items-end sm:min-h-[160px] lg:flex lg:min-h-0">
            <div className="relative h-full w-full max-w-[500px] lg:min-h-[220px]">
              <Image
                src="/icon/Vector.png"
                alt=""
                width={1383}
                height={564}
                className="h-full w-auto max-w-full object-contain object-left-top opacity-100"
                sizes="(max-width: 1024px) 100vw, 500px"
              />
            </div>
          </div>

          <div className="relative z-10 flex min-w-0 max-w-xl flex-col justify-center text-left rtl:text-right md:max-w-lg lg:max-w-xl">
            {sectionLabel && (
              <SectionPreHeader
                icon={<DiamondIcon size={15} className="shrink-0" />}
                className="mb-4 max-[480px]:mb-5 md:mb-7 font-helvetica-neue !text-red-600 !text-base tracking-[1px] font-normal align-middle uppercase"
              >
                {sectionLabel}
              </SectionPreHeader>
            )}
            {moto && (
              <h1 className="font-bebas-neue mb-4 sm:mb-6 text-2xl font-normal leading-[1.1] tracking-tight text-[#1a1a1a] sm:text-4xl lg:text-[61.6px] lg:leading-[68px] uppercase">
                {moto}
              </h1>
            )}
            {message && (
              <p className="font-helvetica-neue mb-6 sm:mb-8 text-justify text-[15px] sm:text-[17px] font-light leading-[1.7] tracking-normal text-neutral-700">
                {message}
              </p>
            )}
            <div className="flex justify-start">
              <Link
                href={buttonLink ?? '/about'}
                className="about-us-btn font-helvetica-neue inline-flex items-center justify-center gap-2 min-w-0 bg-[#E40520] text-white h-12 w-[160px] sm:h-[66px] sm:w-[174.64px] px-6 sm:px-[33px] py-4 sm:py-[21px] font-semibold uppercase text-[10px] sm:text-xs tracking-wide no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E40520] focus-visible:ring-offset-2 transition-opacity hover:opacity-95"
              >
                {buttonLabel}
                {locale == 'ar' ? (
                  <LuArrowUpLeft size={18} aria-hidden className="about-us-btn__arrow" />
                ) : (
                  <LuArrowUpRight size={18} aria-hidden className="about-us-btn__arrow" />
                )}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
